/**
 * International Auctions — data seam.
 *
 * Phase 1 answered every call from in-memory fixtures on a simulated delay, and
 * said in this header that THIS FILE would be the only one to change when the
 * backend landed. It was: every method below keeps the signature, the return
 * shape and the "an unknown slug answers `null`, never throws" behaviour it had
 * on fixtures, so no component, route or template moved.
 *
 * THE FOUR ROUTES (BXS-3627 phase 2, plan §5). Each one serves a whole page, so
 * the several public methods a page calls all read from ONE request:
 *
 *   GET /international-auctions/houses
 *       → { houses[], sale_types[], sales[] }        — the index, the band
 *   GET /international-auctions/houses/{house}
 *       → { house, sales[] }                         — the house page
 *   GET /international-auctions/sales/{house}/{sale}?<filters>&page&per_page
 *       → { sale, lots: Paginated<lot>, filters }    — the catalogue
 *   GET /international-auctions/sales/{house}/{sale}/lots/{lot}
 *       → the lot, with `sale` and prev/next resolved
 *
 * Rules this file must keep:
 *   - No `window` / `document` / `localStorage` — this runs during SSR.
 *   - A failed request answers the SAME empty value an unknown slug does. No
 *     component on this module has an error path — they render a not-found or
 *     an empty state instead — so an exception escaping here would leave a
 *     skeleton spinning forever.
 *   - Sale `status` is DERIVED via `deriveSaleStatus` whenever the API does not
 *     state one, so the ordering can never fork between the two.
 *   - Sales come back already sorted (live → upcoming → preview → closed); the
 *     components document that they must not re-sort.
 *
 * The catalogue filters and the sale ordering stay HERE rather than moving to
 * the query string, because `getSaleCounts` has to count the whole published
 * catalogue whatever the current filter is — so the index request is unfiltered
 * by definition, and filtering it twice (once server-side, once here) would be
 * two implementations of one rule. Lot filtering is the opposite case: it is
 * paginated, so it has to happen where the rows are.
 *
 * Design:  docs/external-cataloge/phase-1/international-auctions-high-level.md
 * Plan:    docs/external-cataloge/phase-2/international-auctions-backend-plan.md
 * Models:  ../pages/international-auctions/_mock/international-auctions.models.ts
 * Ticket:  BXS-3627
 */

import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';

import { HttpService } from 'src/app/Core/interceptors/http/http.service';
import { environment } from 'src/environments/environment';

import {
  compareSalesForDisplay,
  deriveSaleStatus,
  ExternalHouse,
  ExternalLot,
  ExternalLotNumber,
  ExternalLotView,
  ExternalSale,
  ExternalSaleStatus,
  ExternalSaleType,
  ExternalSaleView,
  LotQuery,
  Paginated,
  parseIsoDate,
  resolveTranslation,
  SaleQuery,
} from '../pages/international-auctions/_mock/international-auctions.models';

/** Every read in this file hangs off one root. */
const API_ROOT = `${environment.url}/international-auctions`;

/** Default catalogue page size — matches the lot grid's 4 x 6 layout. */
export const DEFAULT_LOTS_PER_PAGE = 24;

/**
 * `config_key_values` → `international_auctions_homepage_band_enabled` has no
 * endpoint of its own; when it ships it rides along on the homepage bootstrap.
 * Until then the band is on, which is the state the module was signed off in.
 */
const HOMEPAGE_BAND_DEFAULT = true;

/**
 * The distinct values that actually occur in one sale's catalogue. The filter
 * dropdowns are built from this, so they can never offer a value that returns
 * nothing.
 */
export interface LotFilterOptions {
  sexes: string[];
  years: number[];
  horseTypes: string[];
  consignors: string[];
  saleDays: string[];
}

/**
 * A sale as it arrives. The API may already have resolved the view fields —
 * it derives `status` server-side, where the sale's own timezone is known — so
 * every one of them is optional and re-derived here when it is absent.
 */
type ApiSale = ExternalSale & Partial<ExternalSaleView>;

/** The index request, normalised once per response. */
interface IndexBundle {
  houses: ExternalHouse[];
  saleTypes: ExternalSaleType[];
  sales: ExternalSaleView[];
}

/** The catalogue request, normalised once per response. */
interface CatalogueBundle {
  sale: ExternalSaleView | null;
  lots: Paginated<ExternalLot>;
  filters: LotFilterOptions;
}

/** An empty page, in the exact shape the components destructure. */
function emptyPage<T>(perPage: number): Paginated<T> {
  return { items: [], page: 1, per_page: perPage, total: 0, total_pages: 0 };
}

/** Nothing to filter on — an unknown sale, or a sale with no lots yet. */
function emptyFilterOptions(): LotFilterOptions {
  return { sexes: [], years: [], horseTypes: [], consignors: [], saleDays: [] };
}

@Injectable({
  providedIn: 'root',
})
export class InternationalAuctionsService {
  private readonly http = inject(HttpService);

  /**
   * A `BehaviorSubject` so that flipping the flag re-renders a homepage that is
   * already subscribed.
   */
  private readonly homepageBandEnabled$ = new BehaviorSubject<boolean>(
    HOMEPAGE_BAND_DEFAULT
  );

  /**
   * Reads that are still in the air, keyed by request.
   *
   * The index page asks `listHouses`, `listSaleTypes`, `listSales` and
   * `getSaleCounts` in one tick, and the catalogue asks `getSale` and
   * `getLotFilterOptions` in one tick — each group being ONE endpoint. Sharing
   * the in-flight observable collapses each group into a single HTTP call. The
   * entry is dropped the moment the request settles, so this de-duplicates
   * concurrent reads and is never a cache: a later call always refetches.
   */
  private readonly inFlight = new Map<string, Observable<unknown>>();

  /**
   * The one thing here that IS a cache, keyed by sale: the lot-number index
   * behind the "jump to lot" box. A list of numbers does not change while a
   * visitor walks a catalogue, and the whole point of the box is that reaching
   * lot 147 costs one navigation — not one navigation plus a refetch of the
   * book. See `listLotNumbers`.
   */
  private readonly lotNumbers = new Map<
    string,
    Observable<ExternalLotNumber[]>
  >();

  /* ---------------------------------------------------------------- *
   * Houses
   * ---------------------------------------------------------------- */

  /**
   * Every published partner house, in the order the admin set.
   * `GET /international-auctions/houses`
   */
  listHouses(): Observable<ExternalHouse[]> {
    return this.index().pipe(
      map((bundle) =>
        bundle.houses
          .slice()
          .sort(
            (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)
          )
      )
    );
  }

  /**
   * One house by slug, or `null` when the slug is unknown or unpublished — the
   * house page renders a not-found state rather than throwing.
   * `GET /international-auctions/houses/{house}`
   */
  getHouse(slug: string): Observable<ExternalHouse | null> {
    return this.housePage(slug).pipe(map((bundle) => bundle.house));
  }

  /* ---------------------------------------------------------------- *
   * Sale types
   * ---------------------------------------------------------------- */

  /**
   * The sale-type lookup that backs the filter pills. It rides along on the
   * index request rather than costing a round trip of its own.
   */
  listSaleTypes(): Observable<ExternalSaleType[]> {
    return this.index().pipe(map((bundle) => bundle.saleTypes.slice()));
  }

  /* ---------------------------------------------------------------- *
   * Sales
   * ---------------------------------------------------------------- */

  /**
   * All published sales, filtered and already sorted for display
   * (live → upcoming → preview → closed).
   */
  listSales(query: SaleQuery = {}): Observable<ExternalSaleView[]> {
    return this.index().pipe(
      map((bundle) => this.querySales(bundle, query))
    );
  }

  /**
   * That house's sales only. The status filter still applies, and both lists go
   * through the same compare, so the house page and the index can never order
   * the same two sales differently.
   * `GET /international-auctions/houses/{house}`
   */
  listSalesByHouse(
    houseSlug: string,
    status: ExternalSaleStatus | null = null
  ): Observable<ExternalSaleView[]> {
    return this.housePage(houseSlug).pipe(
      map((bundle) =>
        bundle.sales
          .filter((sale) => !status || sale.status === status)
          .sort((a, b) => this.compareSaleViews(a, b))
      )
    );
  }

  /**
   * One sale, with its status derived and its house resolved, or `null` for an
   * unknown slug pair.
   * `GET /international-auctions/sales/{house}/{sale}`
   */
  getSale(
    houseSlug: string,
    saleSlug: string
  ): Observable<ExternalSaleView | null> {
    return this.catalogue(houseSlug, saleSlug).pipe(
      map((bundle) => bundle.sale)
    );
  }

  /**
   * Counts for the "N auctions live now · N more upcoming" line, across every
   * published sale — which is why the index request carries no filter.
   */
  getSaleCounts(): Observable<{
    live: number;
    upcoming: number;
    preview: number;
    closed: number;
    total: number;
  }> {
    return this.index().pipe(
      map((bundle) => {
        const counts = { live: 0, upcoming: 0, preview: 0, closed: 0, total: 0 };
        for (const sale of bundle.sales) {
          counts[sale.status] += 1;
          counts.total += 1;
        }
        return counts;
      })
    );
  }

  /* ---------------------------------------------------------------- *
   * Lots
   * ---------------------------------------------------------------- */

  /**
   * One sale's catalogue: filtered, sorted by `lot_number_sort`, paginated by
   * the API. An unknown sale is an empty page, not an error.
   * `GET /international-auctions/sales/{house}/{sale}`
   * `?lot_number=&search=&sex=&year_foaled=&horse_type=&consignor=&sale_day=&page=&per_page=`
   */
  listLots(
    houseSlug: string,
    saleSlug: string,
    query: LotQuery = {}
  ): Observable<Paginated<ExternalLot>> {
    const perPage = Math.max(
      1,
      Math.trunc(query.per_page ?? DEFAULT_LOTS_PER_PAGE)
    );

    return this.catalogue(houseSlug, saleSlug, {
      ...query,
      per_page: perPage,
    }).pipe(map((bundle) => bundle.lots));
  }

  /**
   * One lot with its sale and house resolved, plus the neighbouring lot numbers
   * in the FULL sale ordering (never the current filtered page), so prev/next
   * walks the whole catalogue. `null` for an unknown lot.
   * `GET /international-auctions/sales/{house}/{sale}/lots/{lot_number}`
   */
  getLot(
    houseSlug: string,
    saleSlug: string,
    lotNumber: string
  ): Observable<ExternalLotView | null> {
    const url =
      `${API_ROOT}/sales/${this.segment(houseSlug)}/${this.segment(saleSlug)}` +
      `/lots/${this.segment(lotNumber)}`;

    return this.read(url).pipe(
      map((body) => {
        const lot = this.one<ExternalLotView>(body, 'lot', 'lot_number');
        if (!lot) {
          return null;
        }
        // The sale rides along on the lot so the co-branded band can render on
        // first paint; its status is derived here for the same reason it is
        // everywhere else — one implementation, one answer.
        return {
          ...lot,
          sale: this.toSaleView(lot.sale as ApiSale, []),
        };
      })
    );
  }

  /**
   * Every published lot number in the sale, in catalogue order — the index
   * behind the "jump to lot" box (CEO review, 18 Aug 2026).
   *
   * CACHED FOR THE SESSION, unlike everything else in this file. `share()`
   * above only coalesces requests that are in the air at the same instant,
   * which is right for a payload that changes as the sale runs. This one is a
   * list of numbers: it does not move while a visitor walks the catalogue, and
   * re-fetching it on every lot page would defeat the point of the box.
   *
   * `GET /international-auctions/sales/{house}/{sale}/lot-numbers`
   */
  listLotNumbers(
    houseSlug: string,
    saleSlug: string
  ): Observable<ExternalLotNumber[]> {
    const url =
      `${API_ROOT}/sales/${this.segment(houseSlug)}/${this.segment(saleSlug)}` +
      `/lot-numbers`;

    const cached = this.lotNumbers.get(url);
    if (cached) {
      return cached;
    }

    const stream = this.read(url).pipe(
      map((body) => this.list<ExternalLotNumber>(body, 'lot_numbers')),
      // An empty answer is either a sale with no lots or a request that failed
      // — `read()` turns every failure into `null`, and both arrive here as
      // `[]`. Neither is worth remembering for the rest of the session, so drop
      // the entry and let the next focus try again.
      tap((rows) => {
        if (rows.length === 0) {
          this.lotNumbers.delete(url);
        }
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.lotNumbers.set(url, stream);
    return stream;
  }

  /**
   * The distinct values present in that sale, for the catalogue filter
   * dropdowns. Built by the API from the whole catalogue rather than from the
   * current page, so a dropdown can never offer a value the next page holds.
   */
  getLotFilterOptions(
    houseSlug: string,
    saleSlug: string
  ): Observable<LotFilterOptions> {
    return this.catalogue(houseSlug, saleSlug).pipe(
      map((bundle) => bundle.filters)
    );
  }

  /* ---------------------------------------------------------------- *
   * Homepage band toggle
   * ---------------------------------------------------------------- */

  /**
   * Unlike every other method this stream stays open, so a homepage subscribed
   * through `| async` reacts the moment the flag is flipped. It has no endpoint
   * yet — see `HOMEPAGE_BAND_DEFAULT`.
   */
  isHomepageBandEnabled(): Observable<boolean> {
    return this.homepageBandEnabled$.asObservable();
  }

  /**
   * PROTOTYPE ONLY — lets the homepage band demonstrate both of its states
   * without an admin screen. Delete with the config key.
   */
  setHomepageBandEnabled(enabled: boolean): void {
    this.homepageBandEnabled$.next(enabled);
  }

  /* ---------------------------------------------------------------- *
   * Requests — nothing below is part of the seam's contract
   * ---------------------------------------------------------------- */

  /** The index request, normalised. Serves four public methods. */
  private index(): Observable<IndexBundle> {
    return this.share(`${API_ROOT}/houses`, () =>
      this.read(`${API_ROOT}/houses`).pipe(
        map((body) => {
          const houses = this.list<ExternalHouse>(body, 'houses');
          const sales = this.list<ApiSale>(body, 'sales').map((sale) =>
            this.toSaleView(sale, houses)
          );
          const declared = this.list<ExternalSaleType>(body, 'sale_types');
          return {
            houses,
            saleTypes: declared.length ? declared : this.saleTypesOf(sales),
            sales,
          };
        })
      )
    );
  }

  /** The house request, normalised. Serves `getHouse` and `listSalesByHouse`. */
  private housePage(
    slug: string
  ): Observable<{ house: ExternalHouse | null; sales: ExternalSaleView[] }> {
    const url = `${API_ROOT}/houses/${this.segment(slug)}`;

    return this.share(url, () =>
      this.read(url).pipe(
        map((body) => {
          const house = this.one<ExternalHouse>(body, 'house', 'slug');
          return {
            house,
            sales: this.list<ApiSale>(body, 'sales').map((sale) =>
              this.toSaleView(sale, house ? [house] : [])
            ),
          };
        })
      )
    );
  }

  /**
   * The catalogue request, normalised. Serves `getSale`, `listLots` and
   * `getLotFilterOptions`; the first and last pass no query, so those two share
   * one call while the lot grid makes its own filtered one.
   */
  private catalogue(
    houseSlug: string,
    saleSlug: string,
    query: LotQuery = {}
  ): Observable<CatalogueBundle> {
    const url = `${API_ROOT}/sales/${this.segment(houseSlug)}/${this.segment(
      saleSlug
    )}`;
    const params = this.lotParams(query);
    const perPage = Number(params['per_page'] ?? DEFAULT_LOTS_PER_PAGE);

    return this.share(this.key(url, params), () =>
      this.read(url, params).pipe(
        map((body) => {
          const sale = this.one<ApiSale>(body, 'sale', 'slug');
          const house = this.one<ExternalHouse>(body, 'house', 'slug');
          return {
            sale: sale ? this.toSaleView(sale, house ? [house] : []) : null,
            lots: this.page<ExternalLot>(body, perPage),
            filters: this.filters(body),
          };
        })
      )
    );
  }

  /**
   * One HTTP read. Every failure — a 404, a 500, an offline browser — answers
   * `null`, which every reader below turns into the same empty value an unknown
   * slug produces. See the header: no component here has an error path.
   */
  private read(
    url: string,
    params: Record<string, string | number> = {}
  ): Observable<unknown> {
    return this.http
      .getWithParams(url, params)
      .pipe(catchError(() => of(null)));
  }

  /**
   * Shares a request that is still in the air with every caller asking for the
   * same thing in the same tick, then forgets it. `refCount` plus the
   * `finalize` means the entry cannot outlive the response.
   */
  private share<T>(key: string, build: () => Observable<T>): Observable<T> {
    const pending = this.inFlight.get(key);
    if (pending) {
      return pending as Observable<T>;
    }

    const shared = build().pipe(
      finalize(() => this.inFlight.delete(key)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.inFlight.set(key, shared);
    return shared;
  }

  /* ---------------------------------------------------------------- *
   * Reading a payload
   * ---------------------------------------------------------------- */

  /**
   * The platform wraps most public payloads in `{ data: … }` — the homepage
   * bootstrap and the search endpoints both do — so unwrap one level before
   * looking for anything. A route that answers with its payload directly is
   * left alone.
   */
  private bag(payload: unknown): Record<string, unknown> {
    if (!payload || typeof payload !== 'object') {
      return {};
    }
    const source = payload as Record<string, unknown>;
    const inner = source['data'] ?? source['Data'];
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
      return inner as Record<string, unknown>;
    }
    return source;
  }

  /** A collection off the payload, tolerating a route that answers bare. */
  private list<T>(payload: unknown, key: string): T[] {
    if (Array.isArray(payload)) {
      return payload as T[];
    }
    const value = this.bag(payload)[key];
    return Array.isArray(value) ? (value as T[]) : [];
  }

  /**
   * A record off the payload. `marker` is the field every such record carries,
   * so a route answering with the record itself rather than wrapping it in a
   * key still resolves.
   */
  private one<T>(payload: unknown, key: string, marker: string): T | null {
    const source = this.bag(payload);
    const nested = source[key];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      return nested as T;
    }
    return typeof source[marker] === 'string' ? (source as T) : null;
  }

  /** The paginated lot page, or an empty one in the shape components expect. */
  private page<T>(payload: unknown, perPage: number): Paginated<T> {
    const source = this.bag(payload);
    const nested = source['lots'];
    const holder = (
      nested && typeof nested === 'object' ? nested : source
    ) as Record<string, unknown>;

    if (!Array.isArray(holder['items'])) {
      return emptyPage<T>(perPage);
    }

    const items = holder['items'] as T[];
    const total = Number(holder['total'] ?? items.length);
    const size = Math.max(1, Number(holder['per_page'] ?? perPage));
    return {
      items,
      page: Math.max(1, Number(holder['page'] ?? 1)),
      per_page: size,
      total,
      total_pages: Number(holder['total_pages'] ?? Math.ceil(total / size)),
    };
  }

  /**
   * The filter dropdown values, every list guarded to an array.
   *
   * Each facet is read under two names: the plural this interface uses, and the
   * singular the API answers with — which is the QUERY PARAMETER the facet
   * feeds, so `sex` is the list that fills `?sex=`. One list, two spellings;
   * accepting both is what keeps the dropdowns populated instead of silently
   * empty, and an empty dropdown looks exactly like a sale with no lots.
   */
  private filters(payload: unknown): LotFilterOptions {
    const source = this.bag(payload)['filters'];
    if (!source || typeof source !== 'object') {
      return emptyFilterOptions();
    }
    const holder = source as Record<string, unknown>;
    const pick = (...keys: string[]): unknown[] => {
      for (const key of keys) {
        if (Array.isArray(holder[key])) {
          return holder[key] as unknown[];
        }
      }
      return [];
    };
    const strings = (...keys: string[]): string[] =>
      pick(...keys).map((value) => String(value));

    return {
      sexes: strings('sexes', 'sex'),
      years: pick('years', 'year_foaled').map((year) => Number(year)),
      horseTypes: strings('horseTypes', 'horse_type'),
      consignors: strings('consignors', 'consignor'),
      saleDays: strings('saleDays', 'sale_day'),
    };
  }

  /* ---------------------------------------------------------------- *
   * Sales — the view fields, and the ordering the components rely on
   * ---------------------------------------------------------------- */

  /**
   * Builds the view a card or header needs.
   *
   * `status` prefers the API's own answer, which is computed against the sale's
   * timezone and its bidding window; `deriveSaleStatus` is the fallback for a
   * payload that carries only the dates, and it is the same function the
   * fixtures ran through, so nothing about the ordering changed.
   */
  private toSaleView(
    sale: ApiSale | null | undefined,
    houses: ExternalHouse[]
  ): ExternalSaleView {
    const source = (sale ?? {}) as ApiSale;
    const house =
      source.house ??
      houses.find(
        (candidate) =>
          this.equalsIgnoreCase(candidate.slug, source.house_slug) ||
          candidate.id === source.house_id
      );

    return {
      ...source,
      status: source.status ?? deriveSaleStatus(source),
      status_is_overridden:
        source.status_override !== null && source.status_override !== undefined,
      house: house as ExternalHouse,
      entered_lot_count: source.entered_lot_count ?? 0,
    };
  }

  /**
   * The sale-type pills, for an index that sends no lookup of its own.
   *
   * Every sale already names its type inline, so the pills can be built from
   * the catalogue itself — and a type derived this way can never offer a pill
   * that matches no sale, which a standalone lookup can. The slug is the
   * English name folded, because that is the value `?type=` carries across a
   * reload and the first thing `matchesSaleType` compares against.
   */
  private saleTypesOf(sales: ExternalSaleView[]): ExternalSaleType[] {
    const found = new Map<number, ExternalSaleType>();

    for (const sale of sales) {
      const type = sale.sale_type;
      const id = sale.sale_type_id;
      const name = type ? resolveTranslation(type, 'en') : null;
      if (!type || id === null || !name || found.has(id)) {
        continue;
      }
      found.set(id, {
        id,
        slug: name.trim().toLowerCase().replace(/\s+/g, '-'),
        name: type,
      });
    }

    return [...found.values()];
  }

  private querySales(bundle: IndexBundle, query: SaleQuery): ExternalSaleView[] {
    const houseSlug = this.normalize(query.house);
    const saleType = this.normalize(query.sale_type);

    return bundle.sales
      .filter((sale) => {
        if (houseSlug && !this.equalsIgnoreCase(sale.house_slug, houseSlug)) {
          return false;
        }
        if (query.status && sale.status !== query.status) {
          return false;
        }
        if (saleType && !this.matchesSaleType(bundle, sale, saleType)) {
          return false;
        }
        return true;
      })
      .sort((a, b) => this.compareSaleViews(a, b));
  }

  /**
   * The filter pills carry the ENGLISH sale-type name, so that is what we match
   * on; the slug is accepted too, so a URL-driven filter also resolves.
   */
  private matchesSaleType(
    bundle: IndexBundle,
    sale: ExternalSale,
    wanted: string
  ): boolean {
    const inline = resolveTranslation(sale.sale_type, 'en');
    if (inline && this.equalsIgnoreCase(inline, wanted)) {
      return true;
    }
    const lookup = bundle.saleTypes.find(
      (type) => type.id === sale.sale_type_id
    );
    if (!lookup) {
      return false;
    }
    const name = resolveTranslation(lookup.name, 'en');
    return (
      (!!name && this.equalsIgnoreCase(name, wanted)) ||
      this.equalsIgnoreCase(lookup.slug, wanted)
    );
  }

  /**
   * The mockup's ordering, straight from `compareSalesForDisplay`. The extra
   * keys only break a genuine tie (two sales of the same status starting the
   * same day), so the list is deterministic across reloads and across SSR.
   */
  private compareSaleViews(a: ExternalSaleView, b: ExternalSaleView): number {
    const primary = compareSalesForDisplay(a, b);
    if (primary !== 0) {
      return primary;
    }
    const byEnd = parseIsoDate(a.ends_on) - parseIsoDate(b.ends_on);
    if (byEnd !== 0) {
      return byEnd;
    }
    return (
      (a.house?.sort_order ?? 0) - (b.house?.sort_order ?? 0) ||
      a.slug.localeCompare(b.slug)
    );
  }

  /* ---------------------------------------------------------------- *
   * Strings
   * ---------------------------------------------------------------- */

  /**
   * The catalogue query, as the API takes it. An absent or blank filter is
   * OMITTED rather than sent empty — `HttpParams` would otherwise put
   * `sex=null` on the wire and the backend would filter on the string "null".
   */
  private lotParams(query: LotQuery): Record<string, string | number> {
    const params: Record<string, string | number> = {};
    const put = (key: string, value: string | null | undefined): void => {
      const text = this.normalize(value);
      if (text) {
        params[key] = text;
      }
    };

    put('lot_number', query.lot_number);
    put('search', query.search);
    put('sex', query.sex);
    put('horse_type', query.horse_type);
    put('consignor', query.consignor);
    put('sale_day', query.sale_day);

    if (typeof query.year_foaled === 'number') {
      params['year_foaled'] = query.year_foaled;
    }
    if (typeof query.page === 'number' && query.page > 1) {
      params['page'] = Math.trunc(query.page);
    }
    if (typeof query.per_page === 'number') {
      params['per_page'] = Math.max(1, Math.trunc(query.per_page));
    }

    return params;
  }

  /** A stable de-duplication key: same URL, same params, same request. */
  private key(url: string, params: Record<string, string | number>): string {
    const query = Object.keys(params)
      .sort()
      .map((name) => `${name}=${params[name]}`)
      .join('&');
    return query ? `${url}?${query}` : url;
  }

  /** A slug straight off the route, so a stray `/` cannot forge a path. */
  private segment(value: string): string {
    return encodeURIComponent((value ?? '').trim());
  }

  /** Trims and drops empties, so `''` behaves the same as "no filter". */
  private normalize(value: string | null | undefined): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private equalsIgnoreCase(
    a: string | null | undefined,
    b: string | null | undefined
  ): boolean {
    if (typeof a !== 'string' || typeof b !== 'string') {
      return false;
    }
    return a.trim().toLowerCase() === b.trim().toLowerCase();
  }
}
